<template>
  <div v-if="loading">Laden ...</div>
  <div v-else-if="error">Er is iets misgegaan, de documenten konden niet worden opgehaald</div>
  <div v-else>
    <a-button-group style="margin-bottom: 1.5rem">
      <a-button :type="allAwaitingButtonType" @click="onDisplayAllAwaitingClick">Documenten in de wachtrij</a-button>
      <a-button :type="allProcessedButtonType" @click="onDisplayAllProcessedClick">Verwerkte documenten</a-button>
    </a-button-group>

    <table class="table" v-if="hasDocuments">
      <thead>
      <tr>
        <th>Document</th>
        <th class="compact">Grootte</th>
        <th class="compact">Type</th>
      </tr>
      </thead>
      <tbody>
      <DocumentMapToTypeComponent
          v-for="document in documents"
          :key="document.id"
          :document="document"
          :editable="false"
      >
      </DocumentMapToTypeComponent>
      </tbody>
    </table>

    <div v-if="!displayOnlyProcessed && !hasDocuments">
      Er staan geen documenten in de wachtrij
    </div>

    <div v-if="displayOnlyProcessed && !hasDocuments">
      Er zijn nog geen verwerkte documenten
    </div>

    <div v-if="!displayOnlyProcessed && hasDocuments && hasDossiers">
      <a-modal title="Documenten uploaden naar Findesk" :closable="false" :visible="displayModal" width="600px"
               class="document-upload-modal">
        <p><b>Selecteer een Findesk dossier:</b></p>
        <a-select
            :default-value="dossierId ? dossierId : undefined"
            placeholder="Selecteer een dossier"
            style="width: 550px"
            @change="handleChangeDossier"
        >
          <a-select-option v-for="dossier in dossiers" :key="dossier.id" :value="dossier.id">
            Dossier aangemaakt op {{ dossier.created_at | iso8601ToDateTime }}
          </a-select-option>
        </a-select>
        <template slot="footer">
          <a-button key="process" @click="processAllDocuments($event)" type="primary">
            Verwerken
          </a-button>
          <a-button key="cancel" @click="closeModal" type="default">
            Annuleren
          </a-button>
        </template>
      </a-modal>

      <a-button type="primary"
                @click="showModal">
        <i class="mdi mdi-check-all"></i> Uploaden naar Findesk
      </a-button>
    </div>
    <div v-if="!displayOnlyProcessed && hasDocuments && !hasDossiers" style="margin-top: 1rem;">
      <a-alert
          message="Er zijn bij nog geen Findesk dossiers bekend.
          Maak deze eerst aan vanuit DossierData om documenten te uploaden."
          banner/>
    </div>
  </div>
</template>

<script>

import attachableApi from "~/api/attachable";
import documentApi from "~/api/document";
import documentTypeApi from "~/api/document_type";
import dossierApi from "~/api/dossier";
import mortgageApi from "~/api/mortgage";
import DocumentMapToTypeComponent from "~/modules/document/components/MapToTypeComponent";
import HumanFilesizeComponent from '~/components/HumanFilesizeComponent';

const moment = require('moment');

export default {
  name: "MortgageDocumentTypeOverviewShowComponent",
  props: {
    mortgageId: {
      type: String,
      required: true,
    }
  },
  components: {
    DocumentMapToTypeComponent,
    HumanFilesizeComponent
  },
  data() {
    return {
      attachables: [],
      customerId: null,
      displayModal: false,
      displayOnlyProcessed: false,
      documentTypes: [],
      dossierId: null,
      dossiers: [],
      error: false,
      loading: false,
      mortgage: null,
      processedDocuments: [],
      queuedDocuments: []
    }
  },
  created() {
    this.loading = true;
    this.initMortgage();
    this.initDocumentTypes();
  },
  computed: {
    documentIds() {
      return this.attachables.map(attachable => attachable.child_id);
    },
    hasDossiers() {
      return this.dossiers.length > 0;
    },
    hasDocuments() {
      return this.documents.length > 0;
    },
    hasDocumentTypes() {
      return this.documentTypes.length > 0;
    },
    allAwaitingButtonType() {
      return this.displayOnlyProcessed ? 'default' : 'primary';
    },
    allProcessedButtonType() {
      return this.displayOnlyProcessed ? 'primary' : 'default';
    },
    documents() {
      if (this.displayOnlyProcessed === true) {
        return this.processedDocuments;
      } else {
        return this.queuedDocuments;
      }
    },
    userId() {
      return window.state.user.id;
    },
  },
  filters: {
    iso8601ToDateTime: function (date) {
      return moment(date, moment.ISO_8601).format('D/MM/YYYY HH:mm')
    }
  },
  methods: {
    handleChangeDossier(value) {
      this.dossierId = value;
    },
    initMortgage() {
      mortgageApi.find(this.mortgageId).then(response => {
        this.mortgage = response.data;
        this.customerId = response.data.customer_id;
        this.initAttachables();
      })
    },
    initAttachables() {
      attachableApi.getByHasAttachableAndIsAttachableType('mortgage', this.mortgageId, 'document').then(response => {
        this.attachables = response.data;
        Promise.all([this.initQueuedDocuments(), this.initProcessedDocuments(), this.initDossiers()]).then(() => {
          this.loading = false;
          this.error = false;
        }).catch(() => {
          this.loading = false;
          this.error = true;
        });
      })
    },
    initDocumentTypes() {
      documentTypeApi.getAllWithHDN().then(response => {
        this.documentTypes = response.data;
      })
    },
    initDossiers() {
      return dossierApi.getByCustomer(this.customerId).then(response => {
        this.dossiers = response.data;
      })
    },
    initQueuedDocuments() {
      return documentApi.findAllQueued(this.documentIds, ['document_type']).then(response => {
        this.queuedDocuments = response.data;
      })
    },
    initProcessedDocuments() {
      return documentApi.getByMortgage(this.mortgageId, ['document_type']).then(response => {
        this.processedDocuments = response.data;
      })
    },
    onDocumentTypeChanged(document, documentType) {
      if (typeof documentType === 'undefined') {
        document.document_type_id = null;
      } else {
        document.document_type_id = documentType.id;
      }

      // Update document
      documentApi.update(document);
    },
    onDisplayAllAwaitingClick() {
      this.displayOnlyProcessed = false;
    },
    onDisplayAllProcessedClick() {
      this.displayOnlyProcessed = true;
    },
    processAllDocuments() {
      documentApi.processDocuments(
          this.documentIds,
          this.dossierId,
          this.mortgageId,
          this.userId,
      ).then(response => {
        this.$message.success({
          content: 'Documenten worden op de achtergrond verwerkt',
          key: 'process_documents',
          duration: 5
        });
      });
      this.closeModal();
    },
    showModal() {
      this.displayModal = true;
    },
    closeModal() {
      this.displayModal = false;
    },
  }
}
</script>

<style scoped>
.mdi-check-all {
  margin-right: 4px;
}
</style>